import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { Translate, withLocalize } from 'react-localize-redux'
import cx from 'classnames'

import { makeStyles } from '@material-ui/core/styles'
import { Button, Typography, Switch, Checkbox, FormControlLabel } from '@material-ui/core'
import { blue } from '@material-ui/core/colors'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined'
import { Alert } from '@material-ui/lab'
import { LoadingButton } from '@mui/lab'

import Select from 'common/components/Select'
import styles from './EditSystemLightsForm.module.scss'
import DataTypes from 'constants/DataTypes'

const useStyles = makeStyles(() => ({
  switch: {
    '& .Mui-checked': {
      color: 'rgba(0,155,229,1)'
    },
    '& .MuiSwitch-colorSecondary': {
      color: 'rgba(255,255,255)'
    },
    '& .MuiSwitch-colorSecondary.Mui-checked': {
      color: 'rgba(0,155,229,1)'
    },
    '& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: 'rgba(0,155,229,1)',
      color: 'rgba(0,155,229,1)'
    },
    '& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-thumb': {
      color: 'rgba(0,155,229,1)'
    },
    '& .MuiSwitch-colorSecondary.Mui-checked:hover': {
      backgroundColor: blue[100]
    },
    '& .MuiSwitch-thumb': {
      width: '21px',
      height: '21px'
    },
    '& .MuiSwitch-track': {
      backgroundColor: 'rgba(33,33,33, 0.4)'
    }
  }
}))

const EditSystemLightsForm = props => {
  const classes = useStyles()

  const [checkedLights, setCheckedLights] = useState(false)
  const [allDeviceSelected, setAllDevicesSelected] = useState(false)

  const { isSubmitting, initialValues, translate, onSubmit, submitError, system } = props
  const { version } = system

  const { handleSubmit, register, control, setValue } = useForm()

  // Default option will be VENTILATION_INDICATOR
  const ledParameterOptions = [
    {
      value: 0,
      label: translate(DataTypes.VENTILATION_INDICATOR) + ' (' + translate('default') + ')'
    },
    { value: 9, label: translate(DataTypes.IAQ) },
    { value: 8, label: translate(DataTypes.COVID_19) },
    { value: 1, label: translate(DataTypes.THERMAL_INDICATOR) },
    { value: 16, label: translate(DataTypes.MOLD_INDICATOR) },
    { value: 2, label: translate(DataTypes.TEMPERATURE) },
    { value: 3, label: translate(DataTypes.HUMIDITY) },
    { value: 4, label: translate(DataTypes.CO2) },
    { value: 5, label: translate(DataTypes.VOCS_INDEX) },
    { value: 6, label: translate(DataTypes.PM25) },
    { value: 7, label: translate(DataTypes.PM10) },
    { value: 10, label: translate(DataTypes.PM1) },
    { value: 11, label: translate(DataTypes.PM4) },
    { value: 12, label: translate(DataTypes.FORMALDEHYDE) },
    { value: 13, label: translate(DataTypes.O3) },
    { value: 14, label: translate(DataTypes.NO2) },
    { value: 15, label: translate(DataTypes.CO) }
  ]

  if (version && Number(version) < 2.7) {
    // Remove MOLD_INDICATOR for ledParameterOptions
    ledParameterOptions.splice(4, 1)
  }

  // Default option will be VENTILATION_INDICATOR (if no ledParameter is set)
  const initialLedParameter = initialValues.ledParameter
    ? ledParameterOptions.find(option => option.value === initialValues.ledParameter)
    : ledParameterOptions[0]

  const handleChangeLightsSwitch = event => {
    setCheckedLights(event.target.checked)
  }

  const handleSelectAllDevices = event => {
    setAllDevicesSelected(event.target.checked)
  }

  const onClickCancel = () => {
    props.hideModal()
  }

  const error = () => {
    const message = translate('submitErrors.unknown')

    return (
      <Alert
        severity='error'
        className={styles.errorMessage}
        style={{ visibility: submitError ? 'visible' : 'hidden' }}
      >
        {message}
      </Alert>
    )
  }

  useEffect(() => {
    setCheckedLights(initialValues.ledOn)
  }, [initialValues.ledOn])

  return (
    <div className={styles.modalContainer}>
      <Typography className={styles.header}>{translate('editDeviceLights')}</Typography>
      <div className={styles.modal}>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={ev => {
            if (ev.key === 'Enter') {
              ev.preventDefault()
            }
          }}
        >
          <div>
            <div className={styles.lightsWrapper}>
              <div className={styles.checkedLightsWrapper}>
                <Typography className={styles.deviceLightsLabel}>
                  {checkedLights
                    ? translate('disableDeviceLights')
                    : translate('enableDeviceLights')}
                </Typography>
                <Switch
                  checked={checkedLights}
                  defaultChecked={checkedLights}
                  onChange={handleChangeLightsSwitch}
                  name='checkedLights'
                  inputProps={{ 'aria-label': 'controlled' }}
                  inputRef={register()}
                  className={classes.switch}
                />
              </div>
              <div className={styles.infoPublicWrapper}>
                <div className={styles.iconPublic}>
                  <InfoOutlinedIcon />
                </div>
                <Typography className={styles.deviceLightsInfoLabel}>
                  {checkedLights ? (
                    <Translate
                      id='disableLightsInfo'
                      options={{ renderInnerHtml: true }}
                    />
                  ) : (
                    <Translate
                      id='enableLightsInfo'
                      options={{ renderInnerHtml: true }}
                    />
                  )}
                </Typography>
              </div>
            </div>
            {initialValues.ledParameter !== undefined && (
              <div className={styles.ledParameterWrapper}>
                <div>
                  <Typography className={styles.ledParameterLabel}>
                    {translate('ledParameterLabel')}
                  </Typography>
                  <div className={styles.cselect}>
                    <Controller
                      render={({ onChange, name }) => (
                        <div>
                          <Select
                            handleChange={e => {
                              setValue(name, e.value)
                            }}
                            className={cx(styles.select)}
                            options={ledParameterOptions}
                            defaultValue={initialLedParameter}
                            disabled={!checkedLights}
                          />
                        </div>
                      )}
                      control={control}
                      name='ledParameter'
                      defaultValue={initialLedParameter ? initialLedParameter.value : null}
                      disabled={!checkedLights}
                    />
                  </div>
                </div>
                <div className={styles.ledParameterInfoWrapper}>
                  <div className={styles.iconPublic}>
                    <InfoOutlinedIcon />
                  </div>
                  <Typography className={styles.deviceLightsInfoLabel}>
                    <Translate
                      id='ledParameterInfo'
                      options={{ renderInnerHtml: true }}
                    />
                  </Typography>
                </div>
              </div>
            )}
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    className={cx(styles.checkbox)}
                    classes={{ checked: styles.checkedBox }}
                    name='selectAll'
                    id='selectAll'
                    onChange={handleSelectAllDevices}
                    inputRef={register()}
                    isChecked={allDeviceSelected}
                  />
                }
                label={translate('applyToAllDevices')}
              />
            </div>
            <div className={styles.infoPublicWrapper}>
              <div className={styles.iconPublic}>
                <InfoOutlinedIcon />
              </div>
              <Typography className={styles.deviceLightsInfoLabel}>
                {translate('applyToAllDevicesInfo')}
              </Typography>
            </div>
            {error()}
            <div className={styles.buttons}>
              <div className={styles.infoUpdateWrapper}>
                <div className={styles.iconPublic}>
                  <FeedbackOutlinedIcon />
                </div>
                <Typography className={styles.deviceLightsInfoLabel}>
                  {translate('deviceUpdateInfo')}
                </Typography>
              </div>
              <div className={styles.buttonWrapper}>
                <LoadingButton
                  type='submit'
                  fullWidth
                  variant='contained'
                  className={styles.buttonAdd}
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  loading={isSubmitting}
                  loadingPosition='end'
                >
                  {translate('saveChanges')}
                </LoadingButton>
              </div>
              <div className={styles.buttonWrapper}>
                <Button
                  fullWidth
                  variant='contained'
                  disableRipple
                  className={styles.buttonCancelar}
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={onClickCancel}
                >
                  {translate('cancel')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

EditSystemLightsForm.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  translate: PropTypes.func.isRequired
}

export default withLocalize(EditSystemLightsForm)
